import { useCallback } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Text from '../../../../../components/Text';
import { conditional, initValue } from '../../../../../utils';
import { EditUI } from '../../../components/BusStop/components';
import FemaleIcon from '../../../../../static/images/GENDER_FEMALE.png';
import MaleIcon from '../../../../../static/images/GENDER_MALE.png';
import XmaleIcon from '../../../../../static/images/GENDER_X.png';
import { eadmissionAPI } from '../../../../../apis';
import StyleButton from '../../../../../components/StyleButton';
import { WtBg } from '../../../components/BusStop/components/Row';
import { i18n } from '../../../../../language';
import ElderlyHomeInfo from '../../../../../components/ElderlyHomeInfo';

const useStyles = makeStyles((theme) => ({
  sup: {
    fontWeight: '700',
    color: '#538FFF',
    fontSize: '0.75rem',
    fontFamily: 'NotoSans-Medium',
    marginLeft: '0.25rem',
  },
  busStop: {
    '&.MuiTableCell-root': {
      padding: '0 !important',
      borderTop: 'none !important',
    },
  },
  admit: {
    color: '#7D86A9',
    padding: '0.08rem 0.35rem',
    fontSize: '0.95rem',
    whiteSpace: 'pre',
    borderRadius: '0.25rem',
    backgroundColor: '#F5FAFE',
  },
  genderIcon: ({ iconSize, genderColor }) => ({
    width: iconSize,
    color: genderColor,
  }),
  age: ({ iconBoxSize, genderColor }) => ({
    color: genderColor,
    width: iconBoxSize,
    fontSize: '1rem',
    fontFamily: 'NotoSans-Bold',
    textAlign: 'center',
  }),
}));

const getStatusColor = (status) => {
  switch (status) {
    case 'PFYA':
    case 'P':
    case 'PWA':
    case 'PWR':
      return '#FA8A82';
    case 'O':
      return '#7FD6F1';
    case 'A':
      return '#feca06';
    case 'RWR':
      return '#f4ff72';
    case 'DRAFT':
      return '#e6d397';
    case 'DL':
    case 'WR':
    case 'REDIR':
      return '#FFFFFF';
    case 'C':
      return '#28D2AB';
    case 'D':
      return '#878687';
    case 'H':
      return '#A6B0C3';
    default:
      return '';
  }
};

const getGenderColor = (gender) => {
  switch (gender) {
    case 'F':
      return '#FF8181';
    case 'M':
      return '#538FFF';
    case 'X':
      return '#7B80FF';

    default:
      return '';
  }
};

const getStatusCw = (status) => {
  switch (status) {
    case 'PFYA':
      return 'Pending FYA';
    case 'P':
      return 'Pending';
    case 'PWA':
      return 'Pending Ward Ack';
    case 'PWR':
      return 'Pending Ward Ready';
    case 'O':
      return 'Pending Adm.';
    case 'A':
      return 'Accepted';
    case 'RWR':
      return i18n.t('BEDSTATUS_RECV_WARD_READY');
    case 'WR':
      return i18n.t('BEDSTATUS_WARD_READY');
    case 'DRAFT':
      return 'Draft';
    case 'DL':
      return 'Declined';
    case 'REDIR':
      return 'Redirected';
    case 'C':
      return 'Completed';
    case 'D':
      return 'Withdrawn';
    case 'H':
      return 'Hold';
    default:
      return '';
  }
};

export const PatientUI = ({
  iconBoxSize = '3.3rem',
  caseNo,
  name,
  gender,
  age,
  hkid = '',
  fromHospCode = '',
  ehAddrInfo = null,
}) => {
  const getGenderIconSize = () => {
    switch (gender) {
      case 'F':
        return '1.85rem';
      case 'M':
        return '2.3rem';
      default:
        return '1.7rem';
    }
  };
  const classes = useStyles({ iconSize: getGenderIconSize(), iconBoxSize, genderColor: getGenderColor(gender) });

  return (
    <Box display="inline-flex" gridGap="0.62rem">
      <Box>
        <Box display="flex" alignItems="center" justifyContent="center">
          {gender === 'M' && <img className={classes.genderIcon} src={MaleIcon} alt="MaleIcon" />}
          {gender === 'F' && <img className={classes.genderIcon} src={FemaleIcon} alt="FemaleIcon" />}
          {gender === 'X' && <img className={classes.genderIcon} src={XmaleIcon} alt="XmaleIcon" />}
        </Box>
        <div className={classes.age}>{initValue(age, '')}</div>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="0.25rem">
        <Box display="flex" gridGap="0.25rem">
          {fromHospCode && (
            <Text fontFamily="NotoSans-Bold" color={getGenderColor(gender)}>
              {initValue(fromHospCode, '')}
            </Text>
          )}
          {caseNo && (
            <Text fontFamily="NotoSans-Bold" color={getGenderColor(gender)}>
              {initValue(caseNo, '')}
            </Text>
          )}
        </Box>
        {hkid && (
          <Text fontFamily="NotoSans-Bold" color={getGenderColor(gender)}>
            {initValue(hkid, '')}
          </Text>
        )}
        <Box display="flex" gridGap="0.3rem">
          <ElderlyHomeInfo ehAddrInfo={ehAddrInfo} />
          <Text color="#2E4469" fontSize="16px" fontFamily="HA_MingLiu, NotoSans-Medium">
            {initValue(name, '')}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

// 根据Admission Type的value找到下拉选项文字。
const getAdmissionTypeText = (admissionTypeList = [], admissionType) => {
  return admissionTypeList.find((item) => item?.value === admissionType)?.text ?? admissionType;
};

const Row = (props) => {
  const {
    eaId,
    targetArriveDtm,
    patient,
    status,
    diagnosis,
    remark,
    columnIndex,
    showDetail,
    transferRequestId,
    admissionType,
    eisSpecCd,
    fromWardLocation,
    fromBedNo,
    openEdit,
    bedstatus,
    handleEditEADM,
    setSuccessAlert,
    handleUpdateSingleData,
    busStopInfo,
    hold,
    patGender,
    version,
    handleAdmissionVersionError,
    setIsShowLoginPop = () => {},
    hkid,
    admRegDtm,
    wardReadyDtm,
    destHospCode,
    destWard,
    cubicleName,
    destWardLocation,
    fromHospCode,
    admissionTypeList,
    ehAddrInfo,
  } = props;
  const history = useHistory();
  const isLogin = useSelector((state) => state.loginInfo.flag);

  const handleEdit = useCallback(() => {
    if (admissionType === 'EADM') {
      if (bedstatus) {
        handleEditEADM();
        history.push(`/bedstatusForm/${eaId}?transferRequestId=${transferRequestId}`);
      } else {
        history.push(`/admissionForm/${eaId}?transferRequestId=${transferRequestId}`);
      }
    } else {
      openEdit && openEdit(props);
    }
  }, [eaId, transferRequestId, openEdit, props]);

  const handleConfirmReady = useCallback(async () => {
    if (!isLogin) {
      setIsShowLoginPop(true);
      return;
    }

    try {
      const params = {
        actionType: 9,
        eaId,
        transferRequestId,
        version,
      };
      await eadmissionAPI.editAdmission(params);
      handleUpdateSingleData(transferRequestId);
      setSuccessAlert(true);
    } catch (error) {
      handleAdmissionVersionError(error);
    }
  }, [eaId, transferRequestId, handleUpdateSingleData]);

  const formatDateTime = (timestamp) => {
    const dateTime = moment(timestamp);
    if (!timestamp || !dateTime.isValid()) {
      return '--';
    }

    return (
      <Box display="flex" flexDirection="column">
        <span>{dateTime.format('D MMMM')}</span>
        <span>{dateTime.format('HH:mm')}</span>
      </Box>
    );
  };

  switch (columnIndex) {
    case 0:
      return (
        <Box display="flex" flexDirection="column" gridGap="0.62rem">
          <Box display="flex" alignItems="center" gridGap="0.62rem">
            <Box
              width="0.625rem"
              height="0.625rem"
              minWidth="0.625rem"
              minHeight="0.625rem"
              borderRadius="50%"
              bgcolor={getStatusColor(status)}
              border={`1px solid ${conditional(
                getStatusColor(status) === '#FFFFFF',
                '#AAAAAA',
                getStatusColor(status)
              )}`}
            />
            {getStatusCw(status)}
          </Box>
          {status === 'PWR' && (
            <StyleButton
              {...{
                text: 'Ward Ready',
                backgroundColor: '#28D2AB',
                type: 'fill',
              }}
              p="0.88rem"
              width="auto"
              onClick={handleConfirmReady}
            />
          )}
        </Box>
      );
    case 1:
      return (
        <Box display="inline-flex">
          <Box
            display="flex"
            p="0.625rem"
            borderRadius="4px"
            bgcolor={WtBg(moment(targetArriveDtm).diff(moment(), 'seconds'))}
            color="#FFFFFF"
          >
            {moment(targetArriveDtm).format('YYYY-MM-DD HH:mm')}
          </Box>
        </Box>
      );
    case 2:
      return initValue(getAdmissionTypeText(admissionTypeList, admissionType), '--');
    case 3:
      return initValue(eisSpecCd, '');
    case 4:
      return (
        <PatientUI iconBoxSize="2rem" {...patient} hkid={hkid} fromHospCode={fromHospCode} ehAddrInfo={ehAddrInfo} />
      );
    case 5:
      return [cubicleName, fromWardLocation, fromBedNo].map((item) => initValue(item, '--')).join('/');
    case 6:
      return (
        <Box style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          {initValue(diagnosis, '--')}
        </Box>
      );
    case 7:
      return initValue(remark, '--');
    case 8:
      return [destHospCode, destWard, destWardLocation].map((item) => initValue(item, '--')).join('/');
    case 9:
      return formatDateTime(wardReadyDtm);
    case 10:
      return formatDateTime(admRegDtm);
    case 11:
      return (
        <EditUI
          open={showDetail}
          handleEdit={handleEdit}
          admissionType={admissionType}
          busStopInfo={busStopInfo}
          hold={hold}
          gender={patGender}
          eaId={eaId}
        />
      );
    default:
      return <div />;
  }
};

export default Row;
